/**
 * "Open full analytics" — claims the team's YOM account in a popup the first
 * time, then links straight into YOM. Rendered only for team owners/admins
 * (the claimer becomes the YOM org owner).
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { analyticsEmbedHelpers } from '@/lib/api';
import { apiErrorMessage } from '@/lib/conflict';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/stores/auth';
import {
  expectedClaimOrigin,
  listenForClaim,
  openClaimWindow,
  showFullAnalytics,
  yomDashboardLink,
} from '@/lib/yom/claim';
import type { ProjectAnalyticsStatus } from '@/lib/yom/types';

export function OpenFullAnalyticsButton({
  projectId,
  status,
}: {
  projectId: string;
  status: ProjectAnalyticsStatus;
}) {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const stopRef = useRef<(() => void) | null>(null);

  useEffect(() => () => stopRef.current?.(), []);

  if (!showFullAnalytics(user, status.canClaim)) return null;

  if (status.claimed) {
    return (
      <Button variant="outline" asChild>
        <a href={yomDashboardLink(status)} target="_blank" rel="noopener noreferrer">
          Open full analytics
          <ExternalLink className="h-4 w-4" aria-hidden />
        </a>
      </Button>
    );
  }

  const start = async () => {
    // Open a blank window inside the click so popup blockers allow it, then
    // point it at the claim link once the backend returns one.
    const first = openClaimWindow('about:blank');
    const placeholder = first.mode === 'blocked' ? null : first.win;
    setBusy(true);
    try {
      const url = await analyticsEmbedHelpers.createClaimLink(projectId);
      const origin = expectedClaimOrigin(url);
      if (!origin) throw new Error('Unexpected analytics address');
      const watch = (win: Window | null) => {
        stopRef.current?.();
        stopRef.current = listenForClaim(
          origin,
          () => {
            stopRef.current = null;
            toast.success('Your analytics account is ready.');
            void qc.invalidateQueries({ queryKey: qk.analyticsStatus(projectId) });
          },
          { source: win ?? undefined }
        );
      };
      if (placeholder && !placeholder.closed) {
        placeholder.location.href = url;
        watch(placeholder);
        return;
      }
      const opened = openClaimWindow(url);
      if (opened.mode !== 'blocked') {
        watch(opened.win);
        return;
      }
      // Pop-ups blocked: a click on the toast is a fresh user gesture, so a
      // plain new tab opens. YOM still messages us through window.opener.
      watch(null);
      toast('Your browser blocked the pop-up', {
        description: 'Open your analytics account in a new tab instead.',
        duration: 20_000,
        action: {
          label: 'Open',
          onClick: () => {
            window.open(url, '_blank');
          },
        },
      });
    } catch (err) {
      placeholder?.close();
      toast.error(apiErrorMessage(err, 'Could not open full analytics. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant="outline" onClick={start} disabled={busy}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
      Open full analytics
      {!busy && <ExternalLink className="h-4 w-4" aria-hidden />}
    </Button>
  );
}
